import { deepKeysToCamel } from '@eventbrite/legacy-infra.transformation-utils';
import {
    EventHeroCarouselData,
    FormattedEvent,
    DestinationEvent,
    EventUrgencySignalsApiData,
    UrgencySignalsKeys,
    EventUrgencySignalsData,
} from '../types';
// import { formatFromAPI } from '@eventbrite/legacy-infra.datetime-fns';
import { EventOpenDiscountApiData } from '../types/events';
import { SnakeCaseKeysToCamelCase } from '@eventbrite/legacy-infra.ts-utils';

const getLocationInfo = (venue?: {
    name: string;
    address?: { localized_area_display: string };
}) => {
    //If we have both name and address add comma separated address
    if (venue?.name && venue?.address?.localized_area_display) {
        //ex. Eventbrite • San Francisco CA
        return `${venue?.name} \u2022 ${venue?.address?.localized_area_display}`;
    }

    return venue?.name || venue?.address?.localized_area_display || '';
};

function mapAdditionalSessionsCount(
    raw: DestinationEvent['additionalSessionsCount'],
): FormattedEvent['additionalSessionsCount'] | undefined {
    if (raw === undefined || raw === null) {
        return undefined;
    }
    if (raw === '9+') {
        return '9+';
    }
    if (typeof raw === 'number') {
        return raw;
    }
    return undefined;
}

export const transformDestinationEvent = (event: DestinationEvent): FormattedEvent => {
    const fullInstanceCount = event?.series?.counts?.current_future || event?.num_children;
    const upcomingRepeatingInstanceCount = fullInstanceCount
        ? fullInstanceCount - 1
        : fullInstanceCount;

    const currency =
        event?.ticket_availability?.minimum_ticket_price?.currency ||
        event?.ticket_availability?.maximum_ticket_price?.currency ||
        '';

    return {
        dedup: event.dedup,
        debugInfo: event?.debug_info?.es_debug_info
            ? {
                  esDebugInfo: event.debug_info.es_debug_info,
              }
            : undefined,
        edgeColor: event?.image?.edge_color || undefined,
        focalPoint: event?.image?.focal_point,
        imageSizes: event?.image?.image_sizes,
        eventSalesStatus: {
            //Default to on_sale in cases where expansion fails or data is not present
            salesStatus: event?.event_sales_status?.sales_status || 'on_sale',
            message: event?.event_sales_status?.message || '',
            messageType: event?.event_sales_status?.message_type || '',
            messageCode: event?.event_sales_status?.message_code || '',
            defaultMessage: event?.event_sales_status?.default_message || '',
        },
        id: event.id,
        imageUrl: event?.image?.url || '',
        isFree: !!event?.ticket_availability?.is_free,
        isOnlineEvent: event.is_online_event,
        isPromoted: !!event?.promoted_listing_metadata,
        bookmarksCount: event?.bookmarks_count,
        isRepeatingEvent: !!event.series_id,
        isSoldOut: event?.event_sales_status?.sales_status === 'sold_out' && !event?.series_id,
        locationInfo: getLocationInfo(event?.primary_venue),
        maxPrice: event?.ticket_availability?.maximum_ticket_price
            ? {
                  currency,
                  maxPriceValue: event?.ticket_availability?.maximum_ticket_price?.value,
              }
            : undefined,
        minPrice: event?.ticket_availability?.minimum_ticket_price
            ? {
                  //Default format. Consuming app
                  //will pass this directly to the eventcard
                  //leaving in place for backwards compatibility
                  currencyFormat: '¤#,##0.00',
                  currency,
                  minPriceValue: event?.ticket_availability?.minimum_ticket_price?.value,
              }
            : undefined,
        name: event.name,
        organizerFollowerCount: event?.primary_organizer?.num_followers || undefined,
        organizerId: event.primary_organizer_id,
        organizerName: event?.primary_organizer?.name || '',
        promotedListingMetadata: deepKeysToCamel(event?.promoted_listing_metadata || {}),
        publicCollections: event?.public_collections?.creator_collections
            ? {
                  creatorCollections: {
                      collections: event.public_collections.creator_collections.collections,
                      objectCount: event.public_collections.creator_collections.object_count,
                  },
              }
            : undefined,
        repeatingInstanceCount: upcomingRepeatingInstanceCount,
        additionalSessionsCount: mapAdditionalSessionsCount(event.additionalSessionsCount),
        savedByYou: !!event.saves?.saved_by_you,
        startDate: event.hide_start_date
            ? undefined
            : event?.series?.next_dates?.[0]?.start.split('T')[0] || event.start_date,
        startTime: event?.series?.next_dates?.[0]?.start.split('T')[1] || event.start_time,
        tags: event.tags?.map(
            (tag: {
                prefix: string;
                tag: string;
                display_name: string;
                localized?: {
                    display_name: string;
                };
            }) => tag.tag,
        ),
        ticketsUrl: event.tickets_url,
        ticketsBy: event.tickets_by,
        timezone: event.timezone,
        urgencySignals: getUrgencySignals(event.urgency_signals),
        openDiscount: getPromoCodeData(event.ticket_availability, {
            timezone: event.timezone,
            currency,
        }),
        specialDiscounts: getSpecialPromoData(event.ticket_availability),
        url: !!event.series_id && event.parent_url ? event.parent_url : event.url,
        venue: deepKeysToCamel(event?.primary_venue || {}),
        widgets: event?.widgets?.map(deepKeysToCamel),
        eventRecommendationSource: event.event_bucket_source,
    };
};

export function getPromoCodeData(
    ticketAvailability: DestinationEvent['ticket_availability'],
    { timezone, currency }: { timezone: string; currency: string },
) {
    const openDiscount = ticketAvailability?.open_discount;

    if (!openDiscount || !ticketAvailability) {
        return undefined;
    }

    const endSalesDateInUtc =
        openDiscount.end_date && timezone
            ? undefined // FIXME formatFromAPI(openDiscount.end_date, timezone)
            : undefined;

    return {
        ...deepKeysToCamel<
            EventOpenDiscountApiData,
            SnakeCaseKeysToCamelCase<EventOpenDiscountApiData>
        >(openDiscount),
        currency,
        endDate: endSalesDateInUtc || ticketAvailability.end_sales_date?.utc || '',
    };
}

export function getSpecialPromoData(ticketAvailability: DestinationEvent['ticket_availability']) {
    if (!ticketAvailability?.has_bogo_tickets) {
        return undefined;
    }

    return {
        hasBogoTickets: ticketAvailability.has_bogo_tickets,
    };
}

export const getUrgencySignals = (apiUrgencySignals?: EventUrgencySignalsApiData | null) => {
    if (!apiUrgencySignals) {
        return undefined;
    }

    const urgencySignals: EventUrgencySignalsData = {
        [UrgencySignalsKeys.fewTickets]: false,
        [UrgencySignalsKeys.salesEndSoon]: false,
        [UrgencySignalsKeys.earlyBird]: false,
        [UrgencySignalsKeys.popular]: false,
        [UrgencySignalsKeys.new]: false,
        [UrgencySignalsKeys.goingFast]: false,
    };

    const activeUrgencySignals = [...apiUrgencySignals.categories, ...apiUrgencySignals.messages];
    activeUrgencySignals.forEach(
        (activeUrgencySignal) => (urgencySignals[activeUrgencySignal] = true),
    );

    return urgencySignals;
};

export function getCarouselWidget(
    event: Pick<FormattedEvent, 'widgets'>,
): EventHeroCarouselData | undefined {
    return event.widgets?.find(({ type }) => type === 'herocarousel') as
        | EventHeroCarouselData
        | undefined;
}

export function hasCarouselWidget(event: Pick<FormattedEvent, 'widgets'>) {
    const carousel = getCarouselWidget(event);
    return !!carousel && carousel.data.slides.length > 1;
}

export function hasTicketsFromExternalProvider({
    ticketsBy = 'Eventbrite',
}: Pick<FormattedEvent, 'ticketsBy'>): boolean {
    return ticketsBy !== 'Eventbrite';
}
